import React from "react";
import { Link } from "react-router-dom";
import Header from "@/components/landing/Header";
import AnnouncementBar from "@/components/landing/AnnouncementBar";
import Footer from "@/components/landing/Footer";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/landing/Breadcrumbs";
import { BLOG } from "@/data/blog";
import { categorySlug } from "@/pages/BlogHubPage";

// Derive unique categories from BLOG
const BLOG_CATEGORIES_LINKS = Array.from(
  new Set(Object.values(BLOG).map((b) => b.category))
).map((cat) => ({
  label: cat,
  to: `/blog/categorie/${categorySlug(cat)}`,
}));

const GROUPS = [
  {
    title: "Hoofdpagina's",
    links: [
      { label: "Home", to: "/" },
      { label: "Functies", to: "/functies" },
      { label: "Doelgroepen", to: "/doelgroepen" },
      { label: "Kennisbank", to: "/kennisbank" },
      { label: "Prijzen", to: "/#pricing" },
      { label: "Support", to: "/support" },
      { label: "FAQ", to: "/faq" },
      { label: "Downloads", to: "/downloads" },
      { label: "Blog", to: "/blog" },
      { label: "Vergelijkingen", to: "/vergelijkingen" },
    ],
  },
  {
    title: "Functies",
    links: [
      { label: "Wedstrijdanalyse", to: "/functies/wedstrijdanalyse" },
      { label: "Hoefsmid", to: "/functies/hoefsmid" },
      { label: "Dierenarts", to: "/functies/dierenarts" },
      { label: "Trainer", to: "/functies/trainer" },
      { label: "Fokkerij", to: "/functies/fokkerij" },
      { label: "Tandarts", to: "/functies/tandarts" },
      { label: "Manege", to: "/functies/manege" },
      { label: "Voeding", to: "/functies/voeding" },
      { label: "Medicatie", to: "/functies/medicatie" },
      { label: "Agenda", to: "/functies/agenda" },
      { label: "Protocol analyse", to: "/functies/protocol-analyse" },
      { label: "Noodnummers paard", to: "/functies/noodnummers" },
      { label: "Weidegang", to: "/functies/weidegang" },
      { label: "Entingen", to: "/functies/entingen" },
      { label: "Ontwormen", to: "/functies/ontwormen" },
      { label: "Gewicht monitoring", to: "/functies/gewicht-monitoring" },
    ],
  },
  {
    title: "Doelgroepen",
    links: [
      { label: "Paardeneigenaren", to: "/doelgroepen/paardeneigenaren" },
      { label: "Manegehouders", to: "/doelgroepen/manegehouders" },
      { label: "Wedstrijdruiters", to: "/doelgroepen/wedstrijdruiters" },
      { label: "Dressuurruiters", to: "/doelgroepen/dressuurruiters" },
      { label: "Springruiters", to: "/doelgroepen/springruiters" },
      { label: "Fokkers", to: "/doelgroepen/fokkers" },
      { label: "Hoefsmeden", to: "/doelgroepen/hoefsmeden" },
      { label: "Dierenartsen", to: "/doelgroepen/dierenartsen" },
      { label: "Trainers", to: "/doelgroepen/trainers" },
    ],
  },
  {
    title: "Kennisbank",
    links: [
      { label: "Paardengezondheid", to: "/kennisbank/paarden-gezondheid" },
      { label: "Voeradvies", to: "/kennisbank/voeradvies" },
      { label: "Hoefverzorging", to: "/kennisbank/hoefverzorging" },
      { label: "Entingen", to: "/kennisbank/entingen" },
      { label: "Fructaan-index", to: "/kennisbank/fructaan-index" },
      { label: "Koliek", to: "/kennisbank/koliek" },
      { label: "Droes", to: "/kennisbank/droes" },
      { label: "Zomereczeem", to: "/kennisbank/zomereczeem" },
    ],
  },
  {
    title: "Blog categorieën",
    links: BLOG_CATEGORIES_LINKS,
  },
  {
    title: "Blog",
    links: Object.entries(BLOG)
      .sort((a, b) => (b[1].datePublished ?? "").localeCompare(a[1].datePublished ?? ""))
      .map(([slug, a]) => ({ label: a.title.split(" — ")[0].split(" | ")[0], to: `/blog/${slug}` })),
  },
  {
    title: "Aandoeningen",
    links: [
      { label: "Koliek", to: "/aandoeningen/koliek" },
      { label: "Hoefbevangenheid", to: "/aandoeningen/hoefbevangenheid" },
      { label: "Droes", to: "/aandoeningen/droes" },
      { label: "West-Nijl", to: "/aandoeningen/west-nijl" },
      { label: "Rhinopneumonie", to: "/aandoeningen/rhinopneumonie" },
      { label: "Zomereczeem", to: "/aandoeningen/zomereczeem" },
    ],
  },
  {
    title: "Entingen",
    links: [
      { label: "Influenza paard", to: "/entingen/influenza-paard" },
      { label: "Tetanus paard", to: "/entingen/tetanus-paard" },
      { label: "Rhinopneumonie paard", to: "/entingen/rhinopneumonie-paard" },
      { label: "West-Nijl paard", to: "/entingen/west-nijl-paard" },
    ],
  },
  {
    title: "Ontworming",
    links: [
      { label: "Ontwormingsschema paard", to: "/ontworming/schema-paard" },
      { label: "EPG test paard", to: "/ontworming/epg-paard" },
    ],
  },
  {
    title: "Disciplines",
    links: [
      { label: "Dressuur", to: "/disciplines/dressuur" },
      { label: "Springen", to: "/disciplines/springen" },
      { label: "Eventing", to: "/disciplines/eventing" },
      { label: "Endurance", to: "/disciplines/endurance" },
      { label: "Western", to: "/disciplines/western" },
    ],
  },
  {
    title: "Paardenrassen",
    links: [
      { label: "Fries paard", to: "/paardenrassen/fries-paard" },
      { label: "KWPN", to: "/paardenrassen/kwpn" },
      { label: "Arabisch Volbloed", to: "/paardenrassen/arabisch-volbloed" },
      { label: "Shetland pony", to: "/paardenrassen/shetland-pony" },
      { label: "Haflinger", to: "/paardenrassen/haflinger" },
    ],
  },
  {
    title: "Juridisch",
    links: [
      { label: "Privacyverklaring", to: "/privacy" },
      { label: "Algemene Voorwaarden", to: "/voorwaarden" },
      { label: "Cookiebeleid", to: "/cookies" },
    ],
  },
];

export const Sitemap = () => {
  const breadcrumbs = [{ name: "Sitemap", path: "/sitemap" }];
  return (
    <main data-testid="sitemap-page" className="bg-background">
      <Seo
        title="Sitemap — alle pagina's | EquiSight"
        description="Volledig overzicht van alle pagina's op EquiSight: functies, doelgroepen, kennisbank, aandoeningen en meer."
        path="/sitemap"
        breadcrumbs={breadcrumbs}
      />
      <AnnouncementBar />
      <Header />
      <Breadcrumbs items={breadcrumbs} />

      <section className="bg-background py-16 lg:py-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
            Sitemap
          </p>
          <h1 className="mt-4 font-display text-5xl font-semibold leading-[1.05] tracking-tight text-[hsl(var(--ink))] sm:text-6xl">
            Alle pagina's op één plek.
          </h1>
          <p className="mt-6 max-w-2xl text-[hsl(var(--ink-soft))]">
            Een overzicht van alle pagina's en modules van EquiSight. Voor
            zoekmachines is ook een{" "}
            <a
              href="/sitemap.xml"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="sitemap-xml-link"
              className="text-[hsl(var(--forest))] underline-offset-4 hover:underline"
            >
              XML-versie
            </a>{" "}
            beschikbaar.
          </p>
        </div>
      </section>

      <section className="bg-background pb-24">
        <div className="mx-auto max-w-6xl px-6 lg:px-10">
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {GROUPS.map((g) => (
              <div
                key={g.title}
                className="rounded-3xl border border-[hsl(var(--border))] bg-[hsl(var(--surface))] p-7"
              >
                <p className="text-xs uppercase tracking-[0.22em] text-[hsl(var(--sand-dark))]">
                  {g.title}
                </p>
                <ul className="mt-5 space-y-2.5">
                  {g.links.map((l) => (
                    <li key={l.to}>
                      <Link
                        to={l.to}
                        className="group inline-flex items-center gap-2 text-[hsl(var(--ink))] hover:text-[hsl(var(--forest))]"
                      >
                        <span className="font-display text-base underline-offset-4 group-hover:underline">
                          {l.label}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default Sitemap;
